import { UIMode } from 'library/ts/enums/UIMode';
import { ContentType } from 'library/ts/model/enums/content/ContentType';
import { PlayerAdapterPlaybackState } from 'library/ts/player/adapter/enums/PlayerAdapterPlaybackState';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { Metrics } from 'library/ts/utilities/measure/Metrics';
import { SkinControl } from 'skin/ts/Skin/model/enums/SkinControl';
import { PageAction } from 'skin/ts/Skin/model/PageAction';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagAction } from 'tag/ts/tag/model/enums/TagAction';
import { PauseAdProvider } from 'tag/ts/tag/model/type/ads/PauseAdProvider';
import { EmbedDevice } from 'tag/ts/tag/model/type/embed/EmbedDevice';
import { EmbedDeviceVersion } from 'tag/ts/tag/model/type/embed/EmbedDeviceVersion';
import { EmbedPlatform } from 'tag/ts/tag/model/type/embed/EmbedPlatform';
import { EmbedType } from 'tag/ts/tag/model/type/embed/EmbedType';

/**
 * @description Class aggregating enums we want to expose via window.SmartTag.enum
 */
export class PublicEnums {
	/**
	 * @description Returns TagEvent enum
	 * @returns {TagEvent}
	 */
	get TagEvent() {
		return TagEvent;
	}

	/**
	 * @description Returns TagAction enum
	 * @returns {TagAction}
	 */
	get TagAction() {
		return TagAction;
	}

	/**
	 * @description Player Adapter Event
	 * @returns {PlayerAdapterEvent}
	 */
	get PlayerEvent() {
		return PlayerAdapterEvent;
	}

	/**
	 * @description Performance Metrics
	 * @returns {Metrics}
	 */
	get Metrics() {
		return Metrics;
	}

	/**
	 * @description Player playback state
	 * @returns {PlaybackState}
	 */
	get PlaybackState() {
		return PlayerAdapterPlaybackState;
	}

	/**
	 * @description Returns ContentType enum
	 * @returns {ContentType}
	 */
	get ContentType() {
		return ContentType;
	}

	/**
	 * @description Returns EmbedType enum
	 * @returns {EmbedType}
	 */
	get EmbedType() {
		return EmbedType;
	}

	/**
	 * @description Returns EmbedPlatform enum
	 * @returns {EmbedPlatform}
	 */
	get EmbedPlatform() {
		return EmbedPlatform;
	}

	/**
	 * @description Returns EmbedDevice enum
	 * @returns {EmbedDevice}
	 */
	get EmbedDevice() {
		return EmbedDevice;
	}

	/**
	 * @description Returns EmbedDeviceVersion
	 * @returns {EmbedDeviceVersion}
	 */
	get EmbedDeviceVersion() {
		return EmbedDeviceVersion;
	}

	/**
	 * @description Returns PageAction enum for Skin related actions
	 * @returns {PageAction}
	 */
	get PageAction() {
		return PageAction;
	}

	/**
	 * @description Returns SkinControl enum for page-driven Skin controls.
	 * Values: `RESTART` (`'restart'`).
	 *
	 * @returns {SkinControl}
	 */
	// NOSONAR: PublicEnums getters intentionally mirror the enum names so hosts get
	// `SmartTag.enums.SkinControl.RESTART` parity with `TagEvent`, `PlaybackState`,
	// `ContentType`, `PageAction`, and every other getter on this class.
	get SkinControl() { // NOSONAR ^ see above
		return SkinControl;
	}

	/**
	 * @description Returns PauseAdProvider enum — vendors of pause-ad inventory (FreeWheel, GAM, Ad Image Service).
	 *
	 * Use when setting the `Skin.PauseAd.params.provider` config field or when calling
	 * `tag.API.TAG.getPauseAd()` directly (e.g. CTV apps without the Skin).
	 *
	 * @returns {PauseAdProvider}
	 */
	get PauseAdProvider() {
		return PauseAdProvider;
	}

	/**
	 * @description Returns UIMode enum — chrome mode passed to `tag.API.TAG.uiMode()`.
	 * Values: `MINI` (`'mini'`), `NORMAL` (`'normal'`), `THEATRE` (`'theatre'`). The
	 * orthogonal persistent-host flag is the optional second `uiMode(mode, alwaysOn)` arg,
	 * not a member here.
	 *
	 * @returns {UIMode}
	 */
	// NOSONAR: PublicEnums getters intentionally mirror the enum names so hosts get
	// `SmartTag.enums.UIMode.MINI` parity with `TagEvent`, `PlaybackState`, `ContentType`,
	// `PauseAdProvider`, and every other getter on this class. Renaming to camelCase here
	// alone would diverge from the established public-API convention.
	get UIMode() { // NOSONAR ^ see above
		return UIMode;
	}
}